import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  CheckCircle2, Circle, BarChart3, FileText, Link2,
  Search, LogOut, ArrowLeft, ExternalLink, Plus, Trash2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import SidebarLayout from '../components/SidebarLayout';

import API from '../utils/api';

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function DeliverablePage() {
  const { user, logout } = useAuth();
  const canWrite = user?.role === 'admin' || user?.effective_permissions?.deliverables === 'edit';
  const navigate = useNavigate();
  const [clientsData, setClientsData] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [clientsRes, statsRes] = await Promise.all([
        axios.get(`${API}/deliverables/clients`, { withCredentials: true }),
        axios.get(`${API}/deliverables/stats`, { withCredentials: true }),
      ]);
      setClientsData(clientsRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const filteredClients = clientsData.filter(c =>
    !search || c.client_name?.toLowerCase().includes(search.toLowerCase()) || c.client_id?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading && !stats) {
    return (<SidebarLayout title="Deliverables" subtitle="Deliverables Tracker"><div className="flex items-center justify-center py-20"><div className="text-white text-lg">Loading...</div></div></SidebarLayout>);
  }

  return (
    <SidebarLayout title="Deliverables" subtitle="Deliverables Tracker">
        {!selectedClient ? (
          <>
            {/* KPI Cards */}
            <section className="mb-8 animate-fadeIn">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard icon={<FileText className="text-indigo-400" />} label="Active Scopes" value={stats?.active_scopes || 0} color="indigo" testId="kpi-active-scopes" />
                <KPICard icon={<CheckCircle2 className="text-emerald-400" />} label="Completed" value={stats?.completed_items || 0} color="emerald" testId="kpi-completed" />
                <KPICard icon={<Circle className="text-amber-400" />} label="Pending" value={(stats?.total_items || 0) - (stats?.completed_items || 0)} color="amber" testId="kpi-pending" />
                <KPICard icon={<BarChart3 className="text-cyan-400" />} label="Completion Rate" value={`${stats?.completion_pct || 0}%`} color="cyan" testId="kpi-rate" />
              </div>
            </section>

            {/* Search */}
            <section className="mb-6 glass-card p-4 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <Input type="text" placeholder="Search clients..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-black/40 border-white/10 text-white placeholder:text-slate-500" data-testid="deliverable-search" />
              </div>
            </section>

            {/* Client Cards */}
            <section className="animate-fadeIn" style={{ animationDelay: '0.2s' }}>
              {filteredClients.length === 0 ? (
                <div className="glass-card p-12 text-center">
                  <p className="text-slate-500">No clients with active scopes. Create a scope from the Client Tracker.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredClients.map((c) => {
                    const isRetainer = c.engagement_type === 'Retainer';
                    const dateRange = isRetainer
                      ? `${formatDate(c.start_date)} — ${formatDate(c.next_renewal_date)}`
                      : `${formatDate(c.start_date)} — ${formatDate(c.end_date)}`;

                    return (
                      <div
                        key={c.scope_id}
                        className="glass-card p-5 cursor-pointer hover:border-indigo-500/30 transition-all group"
                        onClick={() => setSelectedClient(c)}
                        data-testid={`deliverable-client-${c.client_id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-white font-semibold">{c.client_name}</h3>
                            <p className="text-xs font-mono text-cyan-400">{c.client_id}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${isRetainer ? 'bg-cyan-500/10 text-cyan-400' : 'bg-purple-500/10 text-purple-400'}`}>
                            {c.engagement_type}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mb-3">{dateRange}{isRetainer ? ` \u00B7 ${c.renewal_cycle}` : ''}</p>
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs text-slate-500">Progress</span>
                            <span className="text-sm font-bold text-white" data-testid={`completion-${c.client_id}`}>{c.completion_pct}%</span>
                          </div>
                          <Progress value={c.completion_pct} className="h-2" />
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>{c.completed_deliverables}/{c.total_deliverables} deliverables done</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        ) : (
          <ClientDeliverableView
            clientInfo={selectedClient}
            canWrite={canWrite}
            onBack={() => { setSelectedClient(null); fetchData(); }}
          />
        )}
    </SidebarLayout>
  );
}

function KPICard({ icon, label, value, color, testId }) {
  const bgColors = { indigo: 'border-indigo-500/20', emerald: 'border-emerald-500/20', amber: 'border-amber-500/20', cyan: 'border-cyan-500/20' };
  return (
    <div className={`kpi-card border-l-4 ${bgColors[color]}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">{label}</span>
        {icon}
      </div>
      <div className="font-heading text-3xl font-black tracking-tight text-white">{value}</div>
    </div>
  );
}

// ================================
// CLIENT DELIVERABLE DETAIL VIEW
// ================================
function ClientDeliverableView({ clientInfo, canWrite, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proofDialog, setProofDialog] = useState(null);

  const fetchTracking = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/deliverables/client/${clientInfo.client_id}`, {
        withCredentials: true,
        params: { scope_id: clientInfo.scope_id },
      });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [clientInfo.client_id, clientInfo.scope_id]);

  useEffect(() => { fetchTracking(); }, [fetchTracking]);

  if (loading) {
    return <div className="text-slate-500 py-12 text-center">Loading deliverables...</div>;
  }

  // API returns array directly, not object with tracking property
  const tracking = Array.isArray(data) ? data : (data?.tracking || []);
  const totalItems = tracking.reduce((sum, t) => sum + (t.total_quantity || 0), 0);
  const completedItems = tracking.reduce((sum, t) => sum + (t.completed_items?.length || 0), 0);
  const completionPct = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  const scope = data?.scope;

  const isRetainer = clientInfo.engagement_type === 'Retainer';
  const dateRange = isRetainer
    ? `${formatDate(scope?.start_date || clientInfo.start_date)} — ${formatDate(scope?.next_renewal_date || clientInfo.next_renewal_date)}`
    : `${formatDate(scope?.start_date || clientInfo.start_date)} — ${formatDate(scope?.end_date || clientInfo.end_date)}`;

  return (
    <div className="animate-fadeIn">
      {/* Back + Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={onBack} className="text-slate-400 hover:text-white" data-testid="deliverable-back-button">
          <ArrowLeft size={18} className="mr-2" /> Back
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-white">{clientInfo.client_name}</h2>
          <p className="text-xs text-slate-500">
            <span className="font-mono text-cyan-400">{clientInfo.client_id}</span>
            {' \u00B7 '}
            <span className={isRetainer ? 'text-cyan-400' : 'text-purple-400'}>{clientInfo.engagement_type}</span>
            {' \u00B7 '}
            {dateRange}
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-heading font-black text-white" data-testid="client-completion-pct">{completionPct}%</div>
          <div className="text-xs text-slate-500">Completion</div>
        </div>
      </div>

      {/* Overall progress */}
      <div className="glass-card p-4 mb-6">
        <Progress value={completionPct} className="h-3" />
      </div>

      {/* Deliverables grid */}
      {tracking.length === 0 ? (
        <div className="glass-card p-12 text-center text-slate-500">No deliverables to track.</div>
      ) : (
        <div className="space-y-3">
          {tracking.map((t) => {
            const completed = t.completed_items?.length || 0;
            const total = t.total_quantity || 1;
            const pct = Math.round(Math.min(completed / total, 1) * 100);
            const isDone = completed >= total;

            return (
              <div key={t.id} className={`glass-card p-4 ${isDone ? 'border-emerald-500/20' : ''}`} data-testid={`tracking-${t.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      {isDone ? <CheckCircle2 size={16} className="text-emerald-400" /> : <Circle size={16} className="text-slate-500" />}
                      <h3 className="text-white font-medium text-sm">{t.deliverable_type}</h3>
                    </div>
                    <p className="text-xs text-slate-500 ml-6">
                      {t.category}
                      {t.frequency ? ` \u00B7 ${t.frequency}` : ''}
                      {t.platforms?.length > 0 ? ` \u00B7 ${t.platforms.join(', ')}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-white">{completed}<span className="text-slate-500 font-normal">/{total}</span></span>
                    <div className="text-xs text-slate-500">{pct}%</div>
                  </div>
                </div>

                <Progress value={pct} className="h-1.5 mb-3" />

                {/* Completed items list */}
                {t.completed_items?.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    {t.completed_items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-1.5 px-2.5 rounded bg-emerald-500/5 border border-emerald-500/10 group">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <CheckCircle2 size={12} className="text-emerald-400 shrink-0" />
                          <a href={item.proof_url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:text-cyan-300 truncate flex items-center gap-1">
                            {item.description || item.proof_url} <ExternalLink size={10} />
                          </a>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-600">{item.completed_by}</span>
                          <button
                            onClick={async () => {
                              try {
                                await axios.delete(`${API}/deliverables/tracking/${t.id}/proof/${idx}`, { withCredentials: true });
                                toast.success('Removed');
                                fetchTracking();
                              } catch (err) { toast.error('Failed'); }
                            }}
                            className="text-slate-600 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Mark as completed button */}
                {!isDone && canWrite && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setProofDialog(t)}
                    className="bg-white/5 border-white/10 text-slate-300 hover:bg-emerald-500/10 hover:text-emerald-300 hover:border-emerald-500/20 h-8 text-xs"
                    data-testid={`mark-completed-${t.id}`}
                  >
                    <CheckCircle2 size={12} className="mr-1.5" /> Mark as Completed
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {proofDialog && (
        <AddProofDialog
          tracking={proofDialog}
          onClose={() => setProofDialog(null)}
          onAdded={() => { setProofDialog(null); fetchTracking(); }}
        />
      )}
    </div>
  );
}

// ================================
// ADD PROOF DIALOG
// ================================
function AddProofDialog({ tracking, onClose, onAdded }) {
  const [proofUrl, setProofUrl] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!proofUrl.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/deliverables/tracking/${tracking.id}/proof`, {
        proof_url: proofUrl.trim(),
        description: description.trim() || null,
      }, { withCredentials: true });
      toast.success('Deliverable marked as completed');
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const remaining = (tracking.total_quantity || 1) - (tracking.completed_items?.length || 0);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-[#0a0a0f] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white font-heading text-lg">Mark as Completed</DialogTitle>
          <p className="text-sm text-slate-400 mt-1">
            {tracking.deliverable_type} — {remaining} remaining
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Proof Link *</label>
            <Input
              value={proofUrl}
              onChange={(e) => setProofUrl(e.target.value)}
              placeholder="https://instagram.com/reel/... or drive link"
              required
              className="bg-black/40 border-white/10 text-white"
              data-testid="proof-url-input"
            />
            <p className="text-xs text-slate-600 mt-1">Post URL, Google Drive link, or any relevant proof</p>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Description</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description"
              className="bg-black/40 border-white/10 text-white"
              data-testid="proof-description-input"
            />
          </div>
          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
            <Button type="submit" disabled={loading || !proofUrl.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-proof-button">
              {loading ? 'Submitting...' : 'Submit Proof'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
